import Alert from '@/components/Alert/Alert'
import Countdown from '@/components/Countdown'
import GeeTest, { GeetestResultData } from '@/components/GeeTest'
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button'
import { Center } from '@/components/ui/center'
import { Input, InputField } from '@/components/ui/input'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import { sendSMSCode, smsCodeVerify, ThirdPartyLoginProvider } from '@/lib/api/sessionApi'
import { isValidMobile } from '@/lib/utils/isValidMobile'
import { registerOrBindViaThirdPartyThunk } from '@/redux/slices/sessionSlice'
import { useAppDispatch } from '@/redux/store'
import { isFulfilled } from '@reduxjs/toolkit'
import { useRouter } from 'expo-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

export type CompleteRegistrationProps = {
  code: string
  provider: ThirdPartyLoginProvider
}

export default function CompleteRegistration({ code, provider }: CompleteRegistrationProps) {
  const { t } = useTranslation('login')
  const router = useRouter()
  const dispatch = useAppDispatch()

  const [mobileNo, setMobileNo] = useState('')
  const [verificationCode, setVerificationCode] = useState('')

  const [isCountDowning, setIsCountDowning] = useState(false)
  const [loading, setLoading] = useState(false)

  const isMobileValid = isValidMobile(mobileNo)

  const handleSendVerificationCode = async (geetestData: GeetestResultData) => {
    try {
      const response = await sendSMSCode(mobileNo, geetestData)
      if (!response.success) {
        return Alert.alert(response?.errors?.join(', ') ?? t('completeRegistration.sendCodeFailed'))
      }

      setIsCountDowning(true)
    } catch (error) {
      console.error(error)
      Alert.alert(t('completeRegistration.sendCodeFailed'))
    }
  }

  const handleSubmit = async () => {
    if (!verificationCode || !isMobileValid || loading) return
    setLoading(true)
    try {
      const verifyResult = await smsCodeVerify(mobileNo, verificationCode)
      if (!verifyResult.success) {
        Alert.alert(t('completeRegistration.codeVerificationFailed'))
        return
      }

      const result = await dispatch(registerOrBindViaThirdPartyThunk({ code, provider, mobileNo }))
      if (isFulfilled(result)) {
        if (router.canDismiss()) router.dismissAll()
        router.replace('/forms')
      } else {
        Alert.alert(t('completeRegistration.registerOrBindFailed'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Center>
      <VStack space="3xl" className="p-8 max-w-[540] w-full">
        <Text>{t('completeRegistration.tips')}</Text>

        <VStack space="lg">
          <Input size="lg">
            <InputField
              placeholder={t('completeRegistration.mobilePlaceholder')}
              value={mobileNo}
              onChangeText={setMobileNo}
              lineBreakModeIOS="clip"
            />
          </Input>
          <View className="relative">
            <Input size="lg">
              <InputField
                placeholder={t('completeRegistration.codePlaceholder')}
                value={verificationCode}
                onChangeText={setVerificationCode}
                maxLength={6}
                lineBreakModeIOS="clip"
              />
            </Input>
            <View className="absolute right-4 top-0 h-full justify-center">
              <GeeTest type="sms" onSuccess={handleSendVerificationCode}>
                {trigger => (
                  <Button variant="link" isDisabled={isCountDowning || !isMobileValid} onPress={trigger}>
                    <ButtonText>
                      <Countdown initial={60} isRunning={isCountDowning} setIsRunning={setIsCountDowning}>
                        {value => (isCountDowning ? `${value}s` : t('completeRegistration.sendCode'))}
                      </Countdown>
                    </ButtonText>
                  </Button>
                )}
              </GeeTest>
            </View>
          </View>
        </VStack>
        <Button size="lg" onPress={handleSubmit} isDisabled={!isMobileValid || !verificationCode || loading}>
          {loading && <ButtonSpinner />}
          <ButtonText>{t('completeRegistration.bindMobileNo')}</ButtonText>
        </Button>
      </VStack>
    </Center>
  )
}
